import { useState, useEffect, useRef, useId } from 'react'
import { X } from 'lucide-react'
import { generateMachineName } from './hooks/useBuilderObjectTypes'
import { useBuilderSites } from './hooks/useBuilderSites'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'

/**
 * Modale de création d'un TYPE DE CONTENU (`builder_def` de kind `object`).
 *
 * Quatre champs :
 *  - name        — libellé humain ("Actualité")
 *  - type        — machine name dérivé du libellé, éditable (snake_case)
 *  - site        — le site auquel le type appartient : ses contenus y naissent
 *                  et son modèle y est servi. Obligatoire : un type sans site
 *                  serait commun à TOUS les sites, geste réservé à
 *                  l'administration transverse (le back le refuse sinon).
 *  - definition  — YAML brut, pré-rempli avec un squelette minimal
 *
 * Le YAML squelette inclut un seul champ `title` pour démarrer ; l'auteur
 * complète depuis l'onglet « Schéma » du type, puis lui pose un modèle depuis
 * l'onglet « Modèle ».
 */
const SKELETON_YAML = (name) => `name: ${name}
description: ''
primaryField: title

defaultData:
  title: ''

fields:
  - { key: title, label: 'Titre', type: text }
`

export const BuilderTypeCreateModal = ({ onClose, onCreate, defaultSiteId = null }) => {
  const scrim = useScrimDismiss(onClose)
  const { sites, loading: sitesLoading, error: sitesError } = useBuilderSites()
  const [name, setName] = useState('')
  const [machineName, setMachineName] = useState('')
  const [touchedMachine, setTouchedMachine] = useState(false)
  const [siteId, setSiteId] = useState(defaultSiteId ? String(defaultSiteId) : '')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)
  const titleId = useId()
  const nameId = useId()
  const machineId = useId()
  const machineHelpId = useId()
  const siteFieldId = useId()
  const siteHelpId = useId()

  useEffect(() => { inputRef.current?.focus() }, [])

  useEscapeClose(onClose)

  // Maintain machine name in sync with the label unless the user has typed
  // into it explicitly (in which case we respect their value).
  useEffect(() => {
    if (!touchedMachine) {
      setMachineName(generateMachineName(name))
    }
  }, [name, touchedMachine])

  // Un seul site visible : il est présélectionné — il n'y a rien à choisir.
  useEffect(() => {
    if (!siteId && sites.length === 1) setSiteId(String(sites[0].id))
  }, [sites, siteId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedMachine = machineName.trim()
    if (!trimmedName || !trimmedMachine || !siteId) return

    setCreating(true)
    setError(null)
    try {
      await onCreate({
        name: trimmedName,
        type: trimmedMachine,
        definition: SKELETON_YAML(trimmedName),
        siteId: Number(siteId),
      })
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.')
      setCreating(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      {...scrim}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 id={titleId} className="text-base font-semibold text-gray-800">Créer un type de contenu</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label htmlFor={nameId} className="block text-xs font-medium text-gray-500 mb-1.5">
              Nom (libellé)
            </label>
            <input
              id={nameId}
              ref={inputRef}
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="ex : Actualité"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor={machineId} className="block text-xs font-medium text-gray-500 mb-1.5">
              Identifiant machine (snake_case)
            </label>
            <input
              id={machineId}
              type="text"
              value={machineName}
              aria-describedby={machineHelpId}
              onChange={e => {
                setMachineName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                setTouchedMachine(true)
              }}
              placeholder="ex : actualite"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
            />
            <p id={machineHelpId} className="text-xs text-gray-400 mt-1">
              Utilisé comme clé technique. Ne peut plus être changé après création.
            </p>
          </div>

          <div>
            <label htmlFor={siteFieldId} className="block text-xs font-medium text-gray-500 mb-1.5">
              Site
            </label>
            {sitesLoading ? (
              <p className="text-xs text-gray-400">Chargement des sites…</p>
            ) : sitesError ? (
              <p className="text-xs text-red-600" role="alert">Impossible de charger la liste des sites.</p>
            ) : (
              <select
                id={siteFieldId}
                value={siteId}
                onChange={e => setSiteId(e.target.value)}
                aria-describedby={siteHelpId}
                required
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
              >
                <option value="">Choisir un site…</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            <p id={siteHelpId} className="text-xs text-gray-400 mt-1">
              Les contenus de ce type naissent sur ce site, et son modèle y est servi.
            </p>
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
            Un schéma minimal (1 champ <span className="font-mono">title</span>) sera
            créé. Tu pourras ajouter d'autres champs depuis l'onglet « Schéma », puis
            poser le modèle qui affiche les contenus depuis l'onglet « Modèle ».
          </div>

          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !machineName.trim() || !siteId || creating}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
